import { FormEvent } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Alert, AlertDescription } from '../../components/ui/alert';
import type { LoginFormProps } from '../../types/loginTemplate';

/**
 * Reusable login form component.
 * Contains only the form UI — no auth logic.
 */
export default function LoginForm({
  username,
  password,
  showPassword,
  error,
  loading,
  isProgramLogin,
  urlProgramCode,
  onUsernameChange,
  onPasswordChange,
  onTogglePassword,
  onSubmit,
}: LoginFormProps) {
  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    onSubmit(e);
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col space-y-3.5">
      {error && (
        <Alert variant="destructive" className="animate-in fade-in slide-in-from-top-2">
          <AlertDescription className="text-sm">{error}</AlertDescription>
        </Alert>
      )}

      {/* Username group */}
      <div className="space-y-1.5">
        <label htmlFor="username" className="text-sm font-medium text-foreground">
          Username
        </label>
        <Input
          id="username"
          type="text"
          placeholder="Enter your username or email"
          value={username}
          onChange={(e) => onUsernameChange(e.target.value)}
          required
          disabled={loading}
          className="h-11 text-base bg-background/60 border-border/70 focus:bg-background focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
          autoComplete="username"
        />
      </div>

      {/* Password group */}
      <div className="space-y-1.5">
        <label htmlFor="password" className="text-sm font-medium text-foreground">
          Password
        </label>
        <div className="relative">
          <Input
            id="password"
            type={showPassword ? 'text' : 'password'}
            placeholder="Enter your password"
            value={password}
            onChange={(e) => onPasswordChange(e.target.value)}
            required
            disabled={loading}
            className="h-11 text-base pr-16 bg-background/60 border-border/70 focus:bg-background focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
            autoComplete="current-password"
          />
          <Button
            type="button"
            variant="ghost"
            size="xs"
            onClick={onTogglePassword}
            disabled={loading}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            aria-pressed={showPassword}
            className="absolute right-1.5 top-1/2 min-h-0 -translate-y-1/2 px-2.5 py-1.5 text-sm text-primary hover:bg-transparent hover:text-primary-hover"
          >
            {showPassword ? 'Hide' : 'Show'}
          </Button>
        </div>
      </div>

      {/* Forgot password */}
      <div className="flex justify-end">
        <a
          href={isProgramLogin && urlProgramCode ? `/p/${urlProgramCode}/forgot-password` : '/forgot-password'}
          className="text-sm text-muted-foreground hover:text-primary transition-colors"
        >
          Forgot password?
        </a>
      </div>

      {/* Sign in button */}
      <Button
        type="submit"
        disabled={loading}
        aria-busy={loading}
        className="w-full h-11 text-base font-medium text-primary-foreground shadow-sm mt-1"
      >
        {loading ? 'Signing in...' : 'Sign In'}
      </Button>
    </form>
  );
}
